import * as React from 'react';
import { Button } from './Button';
import { useEscapeKey } from './Dialogs';
import { getUploadFileExtension } from '../utils/FileUpload.logic';
import './FilePreviewWindow.css';

// 図面一覧・図枠設定の一覧・写真一覧と、背景図面・図枠の登録・編集の画面で共通に使う、画像の拡大表示の窓

const PREVIEWABLE_EXTENSIONS = ['png', 'jpg', 'jpeg'];

/**
 * 拡大表示の窓で開けるファイルか。開けるのは画像（PNG・JPG）だけで、
 * DXFとPDFはサーバーが画像として返さないため、押したらそのままダウンロードする
 */
export function isPreviewableFile(fileName: string): boolean {
  return PREVIEWABLE_EXTENSIONS.includes(getUploadFileExtension(fileName) ?? '');
}

/** ファイル名を押したときの動きを、名前にマウスを重ねたときに出す */
export function getFileOpenTitle(fileName: string): string {
  if (isPreviewableFile(fileName)) {
    return 'クリックで拡大表示';
  }
  return 'クリックでダウンロード';
}

interface IFileNameButtonProps {
  fileName: string;
  className?: string;
  onClick: () => void;
}

/** 一覧のファイル名。押すと拡大表示（DXFとPDFはダウンロード）するので、リンクの見た目にする */
export const FileNameButton: React.FC<IFileNameButtonProps> = function (props) {
  let className = 'FilePreviewWindow-file_name';
  if (props.className) {
    className += ` ${props.className}`;
  }
  return (
    <button
      type="button"
      className={className}
      title={`${props.fileName}（${getFileOpenTitle(props.fileName)}）`}
      onClick={props.onClick}
    >
      {props.fileName}
    </button>
  );
};

// 読み込み中は画像を隠して読み込み中の文言だけを見せる
function getImageClassName(loading: boolean): string {
  if (loading) {
    return 'FilePreviewWindow-image FilePreviewWindow-image_loading';
  }
  return 'FilePreviewWindow-image';
}

interface IFilePreviewWindowProps {
  fileName: string;
  /** 表示に使う画像のアドレス */
  src: string;
  onClose: () => void;
  /** 見出しの行の［ダウンロード］を押したときの動き */
  onDownload: () => void;
  /** 画像を読み終えたときに呼ぶ。一覧の縮小表示を実画像に切り替えるときに使う */
  onLoad?: () => void;
}

/**
 * 画像の拡大表示の窓。見出しの行に［ダウンロード］と［閉じる］を置き、Escキーと背景のクリックでも閉じる
 */
export const FilePreviewWindow: React.FC<IFilePreviewWindowProps> = function (props) {
  // 大きな画像は表示まで時間がかかるため、読み終えるまで読み込み中の文言を出す。
  // 窓は開くたびに作り直すので、読み込み中の状態は開いたときに初めに戻る
  const [loading, setLoading] = React.useState(true);

  useEscapeKey(true, props.onClose);

  let loadingMessage;
  if (loading) {
    loadingMessage = <p className="FilePreviewWindow-loading">画像を読み込み中...</p>;
  }

  return (
    <div className="FilePreviewWindow-background" onClick={() => props.onClose()}>
      <div
        className="FilePreviewWindow-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={props.fileName}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="FilePreviewWindow-header">
          <span className="FilePreviewWindow-title">{props.fileName}</span>
          <div className="FilePreviewWindow-actions">
            <Button type="button" color="white" mini={true} onClick={() => props.onDownload()}>
              ダウンロード
            </Button>
            <Button type="button" color="white" mini={true} onClick={() => props.onClose()}>
              閉じる
            </Button>
          </div>
        </div>
        <div className="FilePreviewWindow-body">
          {loadingMessage}
          <img
            className={getImageClassName(loading)}
            src={props.src}
            alt={props.fileName}
            onLoad={() => {
              setLoading(false);
              props.onLoad?.();
            }}
            onError={() => setLoading(false)}
          />
        </div>
      </div>
    </div>
  );
};
